import React from "react";

const Admin = React.lazy(() => import("../pages/home/admin/Admin"));
const SessionList = React.lazy(() => import("../pages/list/SessionList"));
const ExamList = React.lazy(() => import("../pages/list/ExamList"));
const ClassList = React.lazy(() => import("../pages/list/ClassList"));
const NewClass = React.lazy(() => import("../pages/class/NewClass"));
const EditClass = React.lazy(() => import("../pages/class/EditClass"));
const SubjectList = React.lazy(() => import("../pages/list/SubjectList"));
const StudentList = React.lazy(() => import("../pages/list/StudentList"));
const NewStudent = React.lazy(() => import("../pages/student/NewStudent"));
const EditStudent = React.lazy(() => import("../pages/student/EditStudent"));
const StaffList = React.lazy(() => import("../pages/list/StaffList"));
const NewStaff = React.lazy(() => import("../pages/staff/NewStaff"));
const EditStaff = React.lazy(() => import("../pages/staff/EditStaff"));
const UploadMarks = React.lazy(
  () => import("../pages/upload-marks/UploadMarks"),
);
const UploadGrades = React.lazy(
  () => import("../pages/upload-grades/UploadGrades"),
);
const ExportStudentDocs = React.lazy(
  () => import("../pages/export-docs/ExportStudentDocs"),
);
const ExportStaffDocs = React.lazy(
  () => import("../pages/export-docs/ExportStaffDocs"),
);
const StudentImport = React.lazy(() => import("../pages/StudentImport"));
const StaffImport = React.lazy(() => import("../pages/StaffImport"));
const Timetable = React.lazy(() => import("../pages/timetable/Timetable"));

const FeeStructureList = React.lazy(
  () => import("../pages/list/FeeStructureList"),
);
const FeeStructure = React.lazy(
  () => import("../pages/fee-structure/FeeStructure"),
);
const FeeRecordList = React.lazy(() => import("../pages/list/FeeRecordList"));
const ReceiptList = React.lazy(() => import("../pages/list/ReceiptList"));

const StudentFeeRecord = React.lazy(
  () => import("../pages/fee-record/FeeRecord"),
);
const Progression = React.lazy(
  () => import("../pages/progression/Progression"),
);
const StudentsGeneratedDocList = React.lazy(
  () => import("../pages/list/StudentsGeneratedDocList"),
);
const StaffsGeneratedDocList = React.lazy(
  () => import("../pages/list/StaffsGeneratedDocList"),
);
const UploadAttendance = React.lazy(
  () => import("../pages/upload-attendance/UploadAttendance"),
);
const StaffAttendance = React.lazy(
  () => import("../pages/attendance/StaffAttendance"),
);
const StaffAttendanceReport = React.lazy(
  () => import("../pages/report/StaffAttendanceReport"),
);
const Settings = React.lazy(() => import("../pages/settings/Settings"));
const Accounts = React.lazy(() => import("../pages/accounts/Accounts"));
const AccountStatement = React.lazy(
  () => import("../pages/accounts/AccountStatement"),
);
const StudentAttendance = React.lazy(
  () => import("../pages/attendance/StudentAttendance"),
);
const StudentAttendanceReport = React.lazy(
  () => import("../pages/report/StudentAttendanceReport"),
);
const UserList = React.lazy(() => import("../pages/list/UserList"));
const TransportManagement = React.lazy(
  () => import("../pages/transport/TransportManagement"),
);
const LibraryManagement = React.lazy(
  () => import("../pages/library/LibraryManagement"),
);

export const adminRoutes = [
  {
    path: "/accounts",
    element: <Accounts />,
    title: "Accounts & Payroll",
    description:
      "Track school income, expenses, profit, and staff salary payments.",
    requiredFeature: "allowPayroll",
  },
  {
    path: "/account-statement",
    element: <AccountStatement />,
    title: "Account Statement",
    description: "Review income, expenses, and net balance for a date range.",
    requiredFeature: "allowPayroll",
  },
  {
    path: "/admin",
    element: <Admin />,
    title: "Admin Dashboard",
    description:
      "Central hub to manage students, staff, classes, fees, and academics.",
  },
  {
    path: "/classes",
    element: <ClassList />,
    title: "Class List",
    description: "Access and manage all classes with assigned sections.",
  },
  {
    path: "/classes/:classId",
    element: <EditClass />,
    title: "Edit Class",
    description: "Detailed view of a specific class, subjects, and students.",
  },
  {
    path: "/classes/new",
    element: <NewClass />,
    title: "New Class",
    description: "Create and register a new class with details.",
  },
  {
    path: "/docs-history/staffs",
    element: <StaffsGeneratedDocList />,
    title: "Staff Docs History",
    description: "View history of generated documents and reports.",
    allowedRoles: [
      "admin",
      "teacher",
      "receptionist",
      "coordinator",
      "manager",
      "principal",
      "operator",
    ],
    requiredPermission: "staffDocsExport",
  },
  {
    path: "/docs-history/students",
    element: <StudentsGeneratedDocList />,
    title: "Student Docs History",
    description: "View history of generated documents and reports.",
    allowedRoles: [
      "admin",
      "teacher",
      "receptionist",
      "coordinator",
      "manager",
      "principal",
      "operator",
    ],
    requiredPermission: "studentDocsExport",
  },
  {
    path: "/exams",
    element: <ExamList />,
    title: "Exam List",
    description: "Browse and organize exam schedules and structures.",
  },
  {
    path: "/fee-books",
    element: <FeeStructureList />,
    title: "Fee Book List",
    description: "View fee structures assigned to different classes.",
    requiredFeature: "allowFeeRecords",
  },
  {
    path: "/fee-records",
    element: <FeeRecordList />,
    title: "Fee Record List",
    description: "Track and manage student fee payment records.",
    allowedRoles: [
      "admin",
      "receptionist",
      "teacher",
      "coordinator",
      "manager",
      "principal",
      "operator",
    ],
    requiredFeature: "allowFeeRecords",
    requiredAnyPermission: ["feeRecord", "classFeeRecord"],
  },
  {
    path: "/fee-structure",
    element: <FeeStructure />,
    title: "Fee Structure",
    description: "Define and manage fee structure for classes.",
    requiredFeature: "allowFeeRecords",
  },
  {
    path: "/import-staffs",
    element: <StaffImport />,
    title: "Import Staffs",
    description: "Bulk import staff records from external files.",
    allowedRoles: [
      "admin",
      "teacher",
      "receptionist",
      "coordinator",
      "manager",
      "principal",
      "operator",
    ],
    requiredPermission: "staffImport",
  },
  {
    path: "/import-students",
    element: <StudentImport />,
    title: "Import Students",
    description: "Bulk import student records from external files.",
    allowedRoles: [
      "admin",
      "teacher",
      "receptionist",
      "coordinator",
      "manager",
      "principal",
      "operator",
    ],
    requiredPermission: "studentImport",
  },
  {
    path: "/progression",
    element: <Progression />,
    title: "Progression",
    description: "Manage student progression and promotions across sessions.",
    allowedRoles: [
      "admin",
      "teacher",
      "receptionist",
      "coordinator",
      "manager",
      "principal",
      "operator",
    ],
    requiredFeature: "allowProgression",
    requiredPermission: "progressionManage",
  },
  {
    path: "/receipts",
    element: <ReceiptList />,
    title: "Receipts",
    description: "All the receipts in one place.",
    allowedRoles: [
      "admin",
      "receptionist",
      "teacher",
      "coordinator",
      "manager",
      "principal",
      "operator",
    ],
    requiredFeature: "allowFeeRecords",
    requiredPermission: "feeReceiptView",
  },
  {
    path: "/school/users",
    element: <UserList />,
    title: "User List",
    description: "",
    allowedRoles: [
      "admin",
      "teacher",
      "receptionist",
      "coordinator",
      "manager",
      "principal",
      "operator",
    ],
    requiredFeature: "allowSchoolUsers",
    requiredPermission: "usersView",
  },
  {
    path: "/sessions",
    element: <SessionList />,
    title: "Session List",
    description: "View and manage academic sessions across school years.",
  },
  {
    path: "/settings",
    element: <Settings />,
    title: "Settings",
    description: "Manage account, workspace, and role-specific preferences.",
    allowedRoles: [
      "appOwner",
      "admin",
      "teacher",
      "receptionist",
      "parent",
      "student",
      "principal",
      "manager",
      "coordinator",
      "operator",
    ],
  },
  {
    path: "/staff-attendance",
    element: <StaffAttendance />,
    title: "Staff Attendance",
    description: "View attendance records for staffs.",
    allowedRoles: [
      "admin",
      "receptionist",
      "teacher",
      "coordinator",
      "manager",
      "principal",
      "operator",
    ],
    requiredPermission: "staffAttendanceMark",
  },
  {
    path: "/staff-attendance-report",
    element: <StaffAttendanceReport />,
    title: "Staff Attendance Report",
    description: "Generate and export staff attendance reports.",
    allowedRoles: [
      "admin",
      "receptionist",
      "teacher",
      "coordinator",
      "manager",
      "principal",
      "operator",
    ],
    requiredFeature: "allowStaffAttendanceReport",
    requiredPermission: "staffAttendanceReportView",
  },
  {
    path: "/staffs",
    element: <StaffList />,
    title: "Staff List",
    description: "View and manage teaching and non-teaching staff.",
    allowedRoles: [
      "admin",
      "teacher",
      "receptionist",
      "coordinator",
      "manager",
      "principal",
      "operator",
    ],
    requiredPermission: "staffsView",
  },
  {
    path: "/staffs/:staffId",
    element: <EditStaff />,
    title: "Edit Staff",
    description: "Detailed profile of a staff member with assignments.",
    allowedRoles: [
      "admin",
      "teacher",
      "receptionist",
      "coordinator",
      "manager",
      "principal",
      "operator",
    ],
    requiredPermission: "staffsView",
  },
  {
    path: "/staffs/new",
    element: <NewStaff />,
    title: "Add New Staff",
    description: "Add a new staff member with role and responsibilities.",
    allowedRoles: [
      "admin",
      "teacher",
      "receptionist",
      "coordinator",
      "manager",
      "principal",
      "operator",
    ],
    requiredPermission: "staffCreate",
  },
  {
    path: "/staffs/export-docs",
    element: <ExportStaffDocs />,
    title: "Export Staff Documents",
    description: "Generate and export staff documents and reports.",
    allowedRoles: [
      "admin",
      "teacher",
      "receptionist",
      "coordinator",
      "manager",
      "principal",
      "operator",
    ],
    requiredPermission: "staffDocsExport",
  },

  {
    path: "/student-attendance",
    element: <StudentAttendance />,
    title: "Student Attendance",
    description: "View attendance records for students.",
    allowedRoles: [
      "admin",
      "teacher",
      "receptionist",
      "coordinator",
      "manager",
      "principal",
      "operator",
    ],
    requiredFeature: "allowStudentAttendanceReport",
    requiredAnyPermission: [
      "studentAttendanceMark",
      "classStudentAttendanceMark",
    ],
  },
  {
    path: "/student-attendance-report",
    element: <StudentAttendanceReport />,
    title: "Student Attendance",
    description: "View attendance records for students.",
    allowedRoles: [
      "admin",
      "receptionist",
      "teacher",
      "coordinator",
      "manager",
      "principal",
      "operator",
    ],
    requiredAnyPermission: [
      "studentAttendanceReportView",
      "classStudentAttendanceReportView",
    ],
  },
  {
    path: "/student-fee-records/:ssId",
    element: <StudentFeeRecord />,
    title: "Student Fee Record",
    description: "Detailed fee record for an individual student.",
    allowedRoles: [
      "admin",
      "receptionist",
      "teacher",
      "coordinator",
      "manager",
      "principal",
      "operator",
    ],
    requiredFeature: "allowFeeRecords",
    requiredPermission: ["feeRecord", "classFeeRecord"],
  },
  {
    path: "/students",
    element: <StudentList />,
    title: "Student List",
    description: "View and manage enrolled students with profiles.",
    allowedRoles: [
      "admin",
      "teacher",
      "receptionist",
      "coordinator",
      "manager",
      "principal",
      "operator",
    ],
    requiredAnyPermission: ["studentsView", "classStudentsView"],
  },
  {
    path: "/students/:studentId",
    element: <EditStudent />,
    title: "Edit Student",
    description: "Edit profile of a student.",
    allowedRoles: [
      "admin",
      "teacher",
      "receptionist",
      "coordinator",
      "manager",
      "principal",
      "operator",
    ],
    requiredAnyPermission: ["studentsView", "classStudentsView"],
  },
  {
    path: "/students/export-docs",
    element: <ExportStudentDocs />,
    title: "Export Student Documents",
    description: "Generate and export student documents and reports.",
    allowedRoles: [
      "admin",
      "teacher",
      "receptionist",
      "coordinator",
      "manager",
      "principal",
      "operator",
    ],
    requiredPermission: "studentDocsExport",
  },
  {
    path: "/students/new",
    element: <NewStudent />,
    title: "Add New Student",
    description: "Register a new student with personal and academic details.",
    allowedRoles: [
      "admin",
      "teacher",
      "receptionist",
      "coordinator",
      "manager",
      "principal",
      "operator",
    ],
    requiredAnyPermission: ["studentCreate", "classStudentCreate"],
  },
  {
    path: "/subjects",
    element: <SubjectList />,
    title: "Subject List",
    description: "Manage subjects offered in the school curriculum.",
  },
  {
    path: "/timetable",
    element: <Timetable />,
    title: "School Timetable",
    description: "Plan weekly periods by class, subject, and teacher.",
    allowedRoles: [
      "admin",
      "receptionist",
      "teacher",
      "coordinator",
      "manager",
      "principal",
      "operator",
    ],
    requiredPermission: "timetableManage",
  },
  {
    path: "/library",
    element: <LibraryManagement />,
    title: "Library Management",
    description:
      "Manage your school book collection, student loans, and returns.",
    requiredFeature: "allowLibrary",
  },
  {
    path: "/transport",
    element: <TransportManagement />,
    title: "Transport Management",
    description: "Manage vehicles, routes, stops, and student seats.",
    requiredFeature: "allowTransport",
  },
  {
    path: "/upload-grades",
    element: <UploadGrades />,
    title: "Upload Grades",
    description: "Record and upload student grades for subjects.",
    allowedRoles: [
      "admin",
      "teacher",
      "receptionist",
      "coordinator",
      "manager",
      "principal",
      "operator",
    ],
    requiredAnyPermission: ["scoreUpload", "classScoreUpload"],
  },
  {
    path: "/upload-marks",
    element: <UploadMarks />,
    title: "Upload Marks",
    description: "Enter and upload student marks for exams.",
    allowedRoles: [
      "admin",
      "teacher",
      "receptionist",
      "coordinator",
      "manager",
      "principal",
      "operator",
    ],
    requiredAnyPermission: ["scoreUpload", "classScoreUpload"],
  },
  {
    path: "/upload-attendance",
    element: <UploadAttendance />,
    title: "Upload Attendance",
    description: "Upload daily or bulk attendance records.",
    allowedRoles: [
      "admin",
      "teacher",
      "receptionist",
      "coordinator",
      "manager",
      "principal",
      "operator",
    ],
    requiredAnyPermission: [
      "studentAttendanceMark",
      "classStudentAttendanceMark",
    ],
  },
];
